/* Only loaded by the Help Center page. Existing FAQ pages use NewFaq unchanged. */
customElements.whenDefined('new-faq').then(() => {
  class SupportHelpCenter extends customElements.get('new-faq') {
    connectedCallback() {
      if (this.initialized) return;
      this.config = JSON.parse(this.querySelector('[data-support-config]').textContent);
      this.ui = this.config.ui;
      this.locale = this.lang || 'en';
      this.serverLocale = this.locale;
      this.languageSelect = this.querySelector('[data-support-language]');
      this.languageStatus = this.querySelector('[data-support-language-status]');
      this.querySelector('[data-support-language-control]').hidden = false;
      this.onLanguageChange = this.onLanguageChange.bind(this);
      this.onPageHistoryChange = this.onPageHistoryChange.bind(this);
      this.languageSelect.value = this.locale;
      this.languageSelect.addEventListener('change', this.onLanguageChange);
      super.connectedCallback();
      this.serverTranslation = {
        ui: this.ui,
        faq: Object.fromEntries(this.questions.map((element) => [element.dataset.supportId, {
          question: element.querySelector('[data-support-question]').textContent,
          answer: element.querySelector('[data-faq-answer]').innerHTML
        }]))
      };
      window.addEventListener('popstate', this.onPageHistoryChange);
      const requested = new URL(window.location.href).searchParams.get('support_lang');
      if (requested && this.config.assets[requested] && requested !== this.locale) {
        this.changeLanguage(requested, false);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.languageSelect?.removeEventListener('change', this.onLanguageChange);
      window.removeEventListener('popstate', this.onPageHistoryChange);
      this.translationRequest?.abort();
    }

    onLanguageChange() {
      this.changeLanguage(this.languageSelect.value, true);
    }

    async onPageHistoryChange() {
      const requested = new URL(window.location.href).searchParams.get('support_lang');
      const locale = requested && this.config.assets[requested] ? requested : this.serverLocale;
      if (locale !== this.locale) await this.changeLanguage(locale, false);
      this.onHashChange();
    }

    async changeLanguage(locale, updateUrl) {
      if (!this.config.assets[locale]) return;
      this.translationRequest?.abort();
      const controller = new AbortController();
      this.translationRequest = controller;
      this.languageStatus.textContent = this.ui.language_loading;
      this.setAttribute('aria-busy', 'true');
      try {
        let translation = this.serverTranslation;
        if (locale !== this.serverLocale) {
          const response = await fetch(this.config.assets[locale], { signal: controller.signal });
          if (!response.ok) throw new Error('Translation unavailable');
          translation = await response.json();
        }
        if (controller.signal.aborted) return;
        if (!translation.ui || !translation.faq) {
          throw new Error('Incomplete translation');
        }
        this.applyTranslation(translation, locale);
        if (updateUrl) {
          const url = new URL(window.location.href);
          if (locale === this.serverLocale) url.searchParams.delete('support_lang');
          else url.searchParams.set('support_lang', locale);
          window.history.replaceState(window.history.state, '', url);
        }
        this.languageStatus.textContent = '';
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.languageSelect.value = this.locale;
        this.languageStatus.textContent = this.ui.language_error;
      } finally {
        if (this.translationRequest === controller) this.removeAttribute('aria-busy');
      }
    }

    applyTranslation(translation, locale) {
      this.ui = translation.ui;
      this.locale = locale;
      this.lang = locale;
      this.languageSelect.value = locale;
      const bindings = {
        text: (element, value) => { element.textContent = value; },
        aria: (element, value) => element.setAttribute('aria-label', value),
        placeholder: (element, value) => element.setAttribute('placeholder', value),
        topicTitle: (element, value) => { element.dataset.faqTopicTitle = value; },
        query: (element, value) => { element.dataset.faqSearchQuery = value; }
      };
      for (const [binding, apply] of Object.entries(bindings)) {
        const attribute = `data-support-${binding.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
        this.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const key = element.getAttribute(attribute);
          if (typeof this.ui[key] === 'string') apply(element, this.ui[key]);
        });
      }
      this.questions.forEach((element) => {
        const entry = translation.faq[element.dataset.supportId] || this.serverTranslation.faq[element.dataset.supportId];
        element.querySelector('[data-support-question]').textContent = entry.question;
        const answer = element.querySelector('[data-faq-answer]');
        answer.innerHTML = entry.answer;
        element.dataset.questionText = entry.question;
        element.dataset.searchText = `${entry.question} ${answer.textContent}`;
      });
      this.searchResultsLabel = this.ui.search_results_label;
      this.noResultsLabel = this.ui.no_results_label;
      const schema = this.querySelector('[data-support-schema]');
      schema.textContent = JSON.stringify({
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: this.questions.map((element) => ({
          '@type': 'Question', name: element.querySelector('[data-support-question]').textContent,
          acceptedAnswer: { '@type': 'Answer', text: element.querySelector('[data-faq-answer]').textContent }
        }))
      });
      if (this.classList.contains('is-topic-view') && this.currentTopic) {
        const trigger = this.querySelector(`[data-faq-topic="${CSS.escape(this.currentTopic)}"]`);
        this.showTopic(this.currentTopic, trigger.dataset.faqTopicTitle, { updateUrl: false, scroll: false });
      } else if (this.classList.contains('is-search-view')) {
        this.showSearch(this.searchInput.value.trim(), { scroll: false });
      }
    }

    formatMessage(key, values = {}) {
      return (this.ui[key] || '')
        .replace(/\{\{\s*(count|query)\s*\}\}/g, (_, name) => String(values[name] ?? ''))
        .replace(/__(COUNT|QUERY)__/g, (_, name) => String(values[name.toLowerCase()] ?? ''));
    }

    showTopic(topic, title, options = {}) {
      this.currentTopic = topic;
      super.showTopic(topic, title, options);
      const count = this.questions.filter((question) => !question.hidden).length;
      this.resultSummary.textContent = this.formatMessage(count === 1 ? 'count_one' : 'count_other', { count });
    }

    showSearch(query, options = {}) {
      super.showSearch(query, options);
      const count = this.questions.filter((question) => !question.hidden).length;
      this.resultSummary.textContent = count
        ? this.formatMessage(count === 1 ? 'search_count_one' : 'search_count_other', { count, query })
        : '';
    }

    normalize(value) {
      return String(value || '').toLocaleLowerCase(this.locale).normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss').replace(/æ/g, 'ae').replace(/ø/g, 'o')
        .replace(/œ/g, 'oe').replace(/[^\p{L}\p{N}]+/gu, ' ').replace(/\s+/g, ' ').trim();
    }

    getMeaningfulTokens(value) {
      const byLanguage = {
        en: 'a an and are can do does for how i in is my of on or the to what when where which why with',
        de: 'der die das ein eine einer einen einem den dem des und oder ist sind ich mein meine wie was wo wann warum kann konnen fur mit von zu im in auf',
        fr: 'le la les un une des du de et ou est sont je mon ma mes comment que quel quelle quels quelles quand pourquoi pour avec au aux dans puis peux peut pouvons pouvez peuvent faut dois doit devons devez doivent avoir obtenir faire',
        it: 'il lo la i gli le un uno una di del della e o sono e io mio mia come cosa quale quali quando dove per con in posso puoi puo possiamo potete possono devo devi deve dobbiamo dovete devono avere ottenere fare',
        nl: 'de het een en of is zijn ik mijn hoe wat waar wanneer waarom kan kunnen voor met van naar in op',
        sv: 'en ett och eller ar jag min mitt mina hur vad var nar varfor kan for med av till i pa',
        da: 'en et og eller er jeg min mit mine hvordan hvad hvor hvornar hvorfor kan for med af til i pa',
        pt: 'o a os as um uma de do da dos das e ou eu meu minha como que qual quais quando onde porque para com em no na posso pode podemos podem devo deve devemos obter fazer',
        es: 'el la los las un una unos unas de del y o es son yo mi mis como que cual cuales cuando donde por para con en puedo puede podemos pueden debo debe debemos obtener hacer'
      };
      const stopWords = new Set((byLanguage[this.locale.split('-')[0]] || byLanguage.en).split(' '));
      const tokens = value.split(' ').filter(Boolean);
      const meaningful = tokens.filter((token) => !stopWords.has(token));
      return meaningful.length ? meaningful : tokens;
    }

    canonicalizeToken(token) {
      if (this.locale === 'en') return super.canonicalizeToken(token);
      const aliases = {
        fr: { rembourser: 'remboursement', rembourse: 'remboursement', remboursements: 'remboursement', retourner: 'retour', retours: 'retour', expedier: 'expedition', livrer: 'livraison' },
        it: { restituire: 'reso', restituzione: 'reso', resi: 'reso', spedire: 'spedizione', rimborsare: 'rimborso' }
      };
      return aliases[this.locale.split('-')[0]]?.[token] || token;
    }
  }

  if (!customElements.get('support-help-center')) customElements.define('support-help-center', SupportHelpCenter);
});
